// public/js/features/manual/manual-page.js
// Device manual (wiki). A table of contents plus one detailed section per Superb
// product: hero, overview, use-cases, features, a spec table, a brand-free
// dimensional sketch and - for trackers - a passive battery-life estimate.
//
// The SAME per-product renderer feeds both the full page (#manual / #manual/slug)
// and the pop-up modal opened from the device list / paired-device cards, so the
// content is maintained in exactly one place (manual-data.js).
import { MANUAL, MANUAL_BY_SLUG, MANUAL_GROUPS, SHIPPING_PROCESS } from './manual-data.js';
import { deviceDrawing } from './manual-drawings.js';
import { deviceAssetUrl } from './manual-assets.js';
import { getCurrentUser, isPlatformLevel } from '../../modules/rbac.js';
import { featureEnabled } from '../../modules/features.js';
import { createBatteryPanel } from '../tracker-config/battery-ui.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// A moderate, mostly-parked example config (think an engine stand that sits most
// of the time and moves occasionally) so the estimate shows a long, realistic
// battery life. BLE is off by default; the panel lets you switch it on.
const DEMO_SETTINGS = { reportIntervalMovingMin: 30, reportIntervalParkedMin: 1440, heartbeatHours: 24, tripDetection: true, bleScan: false, bleScanIntervalS: 3600, bleScanWindowS: 35, shockThresholdG: 4 };
// 2 trips/week, each ~10 h with a position every 30 min (~20 positions/trip).
const DEMO_ASSUMPTIONS = { tripsPerDay: 2 / 7, tripLenMin: 600, gpsFixS: 30, conditionsPct: 85 };
const DEMO_BLE = { intervalS: 3600, windowS: 35 };   // hourly scan, 35 s window (off by default)

// Brand-free measurement sketch drawn from the datasheet dimensions. Products
// with a proper redrawn datasheet drawing (manual-drawings.js) use that instead;
// this stays the fallback for everything else.
function dimensionSvg(dims, accent = '#0d6efd') {
  if (!dims) return '';
  if (dims.d != null) { // disc: diameter x thickness
    const s = 120 / dims.d, r = (dims.d * s) / 2, cx = 90, cy = 80;
    return `<svg viewBox="0 0 200 150" width="100%" style="max-width:260px;">
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="${accent}22" stroke="${accent}" stroke-width="2"/>
      <line x1="${cx - r}" y1="${cy}" x2="${cx + r}" y2="${cy}" stroke="${accent}" stroke-width="1" stroke-dasharray="3 3"/>
      <text x="${cx}" y="${cy - r - 6}" text-anchor="middle" font-size="12" fill="currentColor">D ${dims.d} mm</text>
      <text x="${cx + r + 10}" y="${cy + 4}" font-size="12" fill="currentColor">h ${dims.h} mm</text>
    </svg>`;
  }
  const s = 130 / Math.max(dims.l, dims.w), bw = dims.l * s, bh = dims.w * s, x = 30, y = 20;
  return `<svg viewBox="0 0 220 ${bh + 70}" width="100%" style="max-width:280px;">
    <rect x="${x}" y="${y}" width="${bw}" height="${bh}" rx="6" fill="${accent}22" stroke="${accent}" stroke-width="2"/>
    <text x="${x + bw / 2}" y="${y + bh + 20}" text-anchor="middle" font-size="12" fill="currentColor">${dims.l} mm</text>
    <text x="${x + bw + 10}" y="${y + bh / 2}" font-size="12" fill="currentColor">${dims.w} mm</text>
    <text x="${x}" y="${y + bh + 42}" font-size="12" fill="currentColor" opacity="0.8">Thickness ${dims.h} mm</text>
  </svg>`;
}

function heroHtml(m) {
  const url = deviceAssetUrl(m.image);
  if (m.image && url) {
    return `<div class="manual-hero d-flex align-items-center justify-content-center p-3 rounded" style="background:${m.accent}10;">
      <img src="${url}" alt="${esc(m.name)}" style="max-width:100%;max-height:190px;object-fit:contain;" loading="lazy">
    </div>`;
  }
  return `<div class="manual-hero d-flex align-items-center justify-content-center rounded" style="background:${m.accent}14;min-height:190px;">
    <i class="fa-solid ${m.icon}" style="font-size:5rem;color:${m.accent};"></i>
  </div>`;
}

// The reusable per-product body (no outer card / section header) - used by both
// the page section and the modal. Contains a #manual-bat-<slug> mount point.
export function productBodyHtml(m) {
  const specRows = m.specs.map(([k, v]) => `<tr><td class="text-muted pe-3" style="white-space:nowrap;">${esc(k)}</td><td class="fw-medium">${esc(v)}</td></tr>`).join('');
  const featureItems = m.features.map(f => `<li>${m.hasHtmlFeatures ? f : esc(f)}</li>`).join('');
  const useItems = m.useCases.map(u => `<li>${esc(u)}</li>`).join('');
  const drawing = deviceDrawing(m.drawing);
  const dimsBlock = (drawing || m.dimensions)
    ? `<div class="text-center mt-2"><div class="small fw-semibold text-secondary mb-2 text-start">Dimensions</div>${drawing || dimensionSvg(m.dimensions, m.accent)}</div>`
    : '';
  return `
    <div class="row g-4">
      <div class="col-md-4">${heroHtml(m)}</div>
      <div class="col-md-8">
        <p class="text-muted mb-2">${esc(m.tagline)}</p>
        <p>${esc(m.overview)}</p>
        ${useItems ? `<div class="small fw-semibold text-secondary mt-3 mb-1">What it is for</div><ul class="mb-0">${useItems}</ul>` : ''}
      </div>
    </div>
    <hr class="my-4">
    <div class="row g-4">
      <div class="col-md-6">
        <div class="small fw-semibold text-secondary mb-2">Key features</div>
        <ul class="mb-0">${featureItems || '<li class="text-muted">-</li>'}</ul>
      </div>
      <div class="col-md-6">
        <div class="small fw-semibold text-secondary mb-2">Specifications</div>
        <table class="table table-sm align-middle mb-0"><tbody>${specRows}</tbody></table>
        ${dimsBlock}
      </div>
    </div>
    <div class="mt-3" id="manual-bat-${m.slug}"></div>`;
}

// Attach the passive battery estimate (trackers) or static note (others) inside
// a rendered body. `root` is the element containing #manual-bat-<slug>.
export function mountBattery(root, m) {
  const host = root.querySelector(`#manual-bat-${m.slug}`);
  if (!host) return;
  if (m.battery && m.batteryFamily) {
    createBatteryPanel(host, {
      families: [{ key: m.batteryFamily, label: m.name }], familyKey: m.batteryFamily,
      familyLocked: true, hideFamilyRow: true, hideNotes: true, subtitle: 'typical configuration',
      assumptions: DEMO_ASSUMPTIONS, conditionsLocked: true, bleControls: true, ble: DEMO_BLE,
    }).refresh(DEMO_SETTINGS);
  } else if (m.batteryNote) {
    host.innerHTML = `<div class="text-muted small"><i class="fa-solid fa-battery-full me-2 text-success"></i>${esc(m.batteryNote)}</div>`;
  }
}

function sectionHtml(m) {
  return `
    <section id="manual-${m.slug}" class="manual-section mb-5">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h2 class="h4 mb-0"><i class="fa-solid ${m.icon} me-2" style="color:${m.accent};"></i>${esc(m.name)}</h2>
        <a href="#manual/devices" class="small text-muted text-decoration-none"><i class="fa-solid fa-arrow-up me-1"></i>Contents</a>
      </div>
      <div class="card shadow-sm"><div class="card-body">${productBodyHtml(m)}</div></div>
    </section>`;
}

function tocHtml() {
  return `
    <div class="card mb-4">
      <div class="card-body py-3">
        <div class="small fw-semibold text-uppercase text-muted mb-2"><i class="fa-solid fa-list me-2"></i>Contents</div>
        <div class="row g-2">
          ${MANUAL_GROUPS.map(g => `
            <div class="col-sm-4">
              <div class="small text-muted mb-1">${esc(g.title)}</div>
              ${g.keys.map(k => { const m = MANUAL_BY_SLUG[k]; return m ? `<a href="#manual/devices/${m.slug}" class="d-block text-decoration-none mb-1"><i class="fa-solid ${m.icon} me-2" style="color:${m.accent};"></i>${esc(m.name)}</a>` : ''; }).join('')}
            </div>`).join('')}
        </div>
      </div>
    </div>`;
}

// Documentation hub. #manual is an overview that links to sub-pages, each with a
// permalink URL path: #manual/devices (device manual), #manual/devices/<slug> (one
// device), #manual/api (API reference), #manual/shipping (internal, platform
// roles only). Old #manual/<deviceSlug> links still work.
export async function initManualPage(container) {
  const groups = getCurrentUser().groups || [];
  if (!groups.length) { container.innerHTML = `<div class="alert alert-warning m-3">You need a role to view the documentation.</div>`; return; }

  const parts = (window.location.hash || '').split('/').slice(1); // segments after #manual
  const sub = parts[0] || '';

  if (sub === 'api') return renderApiReference(container);
  if (sub === SHIPPING_PROCESS.slug) return renderShippingProcess(container, groups);
  if (sub === 'devices') return renderDeviceManual(container, parts[1] || '');
  if (sub && MANUAL_BY_SLUG[sub]) return renderDeviceManual(container, sub); // back-compat
  return renderHub(container);
}

// ── #manual — overview / table of contents for the whole documentation hub ──
function renderHub(container) {
  const cards = [
    { href: '#manual/devices', icon: 'fa-satellite-dish', accent: '#0d6efd', title: 'Device manual',
      desc: 'What each Superb device does, how it is used, its specifications and estimated battery life.' },
    { href: '#manual/api', icon: 'fa-code', accent: '#7c3aed', title: 'API reference',
      desc: 'Read your asset data and receive webhooks programmatically. Keys are managed in Settings - API access.' },
  ];
  // Internal operations pages are only listed for platform roles, support
  // included - they run the bench (the route itself is gated too, so a shared
  // link shows an access-denied message).
  if (isPlatformLevel(getCurrentUser().groups || [])) {
    cards.push({ href: `#manual/${SHIPPING_PROCESS.slug}`, icon: 'fa-box-open', accent: '#d97706', title: SHIPPING_PROCESS.title, internal: true,
      desc: 'The bench checklist from order received to parcel handed to the carrier: configuration, SIM pairing, APN, housing, labels, dispatch.' });
  }
  container.innerHTML = `
    <div class="container-fluid py-3" style="max-width:1000px;">
      <div class="mb-4">
        <h1 class="h4 mb-1"><i class="fa-solid fa-book me-2"></i>Documentation</h1>
        <p class="text-muted mb-0">Guides and references for Superb Tracking.</p>
      </div>
      <div class="row g-3">
        ${cards.map(c => `
          <div class="col-md-6">
            <a href="${c.href}" class="text-decoration-none">
              <div class="card shadow-sm h-100 manual-hub-card">
                <div class="card-body d-flex align-items-start gap-3">
                  <div class="rounded d-flex align-items-center justify-content-center" style="width:48px;height:48px;background:${c.accent}18;flex:0 0 auto;">
                    <i class="fa-solid ${c.icon}" style="color:${c.accent};font-size:1.4rem;"></i>
                  </div>
                  <div>
                    <div class="h6 mb-1 text-body">${c.title}${c.internal ? ' <span class="badge-soft badge-soft-amber ms-1 align-middle">Internal</span>' : ''}</div>
                    <div class="small text-muted">${c.desc}</div>
                  </div>
                </div>
              </div>
            </a>
          </div>`).join('')}
      </div>
    </div>`;
}

// ── #manual/devices[/<slug>] — the device manual (TOC + one section per product) ──
function renderDeviceManual(container, slug) {
  container.innerHTML = `
    <div class="container-fluid py-3" style="max-width:1000px;">
      <div class="mb-3">
        <a href="#manual" class="small text-muted text-decoration-none"><i class="fa-solid fa-arrow-left me-1"></i>Documentation</a>
        <h1 class="h4 mb-1 mt-1"><i class="fa-solid fa-satellite-dish me-2"></i>Device manual</h1>
        <p class="text-muted mb-0">Everything about the Superb devices - what each one does, how it is used, its specifications and estimated battery life.</p>
      </div>
      ${tocHtml()}
      ${MANUAL.map(sectionHtml).join('')}
    </div>`;

  MANUAL.forEach(m => mountBattery(container, m));
  scrollToSlug(slug);
}

// Pop-up modal with one product's content - opened from device rows / paired
// devices. Uses the exact same body renderer as the page (single source).
export function openManualModal(slug) {
  const m = MANUAL_BY_SLUG[slug];
  if (!m) { window.location.hash = '#manual'; return; }
  document.getElementById('manualModal')?.remove();
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal fade" id="manualModal" tabindex="-1"><div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="fa-solid ${m.icon} me-2" style="color:${m.accent};"></i>${esc(m.name)}</h5>
          <button class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body" id="manualModalBody">${productBodyHtml(m)}</div>
        <div class="modal-footer">
          <a href="#manual/devices/${m.slug}" class="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">Open full manual</a>
          <button class="btn btn-sm btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div></div>`);
  const el = document.getElementById('manualModal');
  mountBattery(el.querySelector('#manualModalBody'), m);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
  new bootstrap.Modal(el).show();
}

// Deep-link: #manual/devices/<slug> scrolls to and highlights that section.
function scrollToSlug(slug) {
  if (!slug) return;
  const el = document.getElementById(`manual-${slug}`);
  if (!el) return;
  setTimeout(() => {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.querySelector('.card')?.classList.add('border-primary');
    setTimeout(() => el.querySelector('.card')?.classList.remove('border-primary'), 2000);
  }, 80);
}

// ── #manual/shipping — internal: preparing trackers for shipment ───────────────
// Bench checklist for platform staff (docs/tracker-shipping.md). Content lives in
// manual-data.js (SHIPPING_PROCESS); steps are numbered continuously across the
// phases so the notes can refer to them by number. Not a feature - it is gated by
// role only, like the distribution page it ends on.
function renderShippingProcess(container, groups) {
  if (!isPlatformLevel(groups)) {
    container.innerHTML = `<div class="alert alert-danger m-3">Access denied - platform roles only.</div>`;
    return;
  }
  const p = SHIPPING_PROCESS;
  let n = 0;
  const phaseHtml = (ph) => `
    <div class="card shadow-sm mb-3"><div class="card-body">
      <h2 class="h6 text-uppercase text-muted mb-3"><i class="fa-solid ${ph.icon} me-2"></i>${esc(ph.title)}</h2>
      <ol class="list-unstyled mb-0" start="${n + 1}">
        ${ph.steps.map(st => { n += 1; return `
        <li class="d-flex align-items-start gap-3 mb-2">
          <span class="badge rounded-pill text-bg-secondary flex-shrink-0" style="min-width:2em;">${n}</span>
          <span>${st.text}</span>
        </li>`; }).join('')}
      </ol>
    </div></div>`;
  // Related pages: the order the last step scans into, and (where the feature is
  // switched on) the tracker configuration the first step sets.
  const related = [
    `<a href="#distribution" class="text-decoration-none"><i class="fa-solid fa-boxes-stacked me-1"></i>Device distribution</a>`,
    featureEnabled('trackerConfig') ? `<a href="#tracker-config" class="text-decoration-none"><i class="fa-solid fa-sliders me-1"></i>Tracker configuration</a>` : '',
  ].filter(Boolean);

  container.innerHTML = `
    <div class="container-fluid py-3" style="max-width:1000px;">
      <div class="mb-3">
        <a href="#manual" class="small text-muted text-decoration-none"><i class="fa-solid fa-arrow-left me-1"></i>Documentation</a>
        <h1 class="h4 mb-1 mt-1"><i class="fa-solid fa-box-open me-2"></i>${esc(p.title)}
          <span class="badge-soft badge-soft-amber ms-2 align-middle">Internal</span></h1>
        <p class="text-muted mb-0">${esc(p.intro)}</p>
      </div>

      ${p.phases.map(phaseHtml).join('')}

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted mb-2"><i class="fa-solid fa-circle-info me-2"></i>Notes</h2>
        <ul class="mb-0">${p.notes.map(t => `<li class="mb-1">${t}</li>`).join('')}</ul>
      </div></div>

      <div class="small text-muted">Related: ${related.join(' &middot; ')}</div>
    </div>`;
}

// ── #manual/api — customer API reference (docs/api-access.md) ──────────────────
// Every fact on this page is taken from the implementation (router.mjs public
// branch, handlers/public-api.mjs, utils/webhooks/deliver.mjs, utils/rate-limit)
// — keep them in sync when the API changes.
function renderApiReference(container) {
  const base = `${location.origin}/api/v1/public`;
  const code = (s) => `<pre class="bg-body-tertiary border rounded p-3 mb-3" style="overflow-x:auto;"><code>${esc(s)}</code></pre>`;
  const endpoint = (method, path, scope) => `
    <div class="d-flex align-items-center flex-wrap gap-2 mb-2 mt-4">
      <span class="badge text-bg-success">${method}</span><code class="fs-6">${esc(path)}</code>
      <span class="badge text-bg-light border text-muted fw-normal">scope: ${esc(scope)}</span>
    </div>`;
  const paramRow = (name, desc) => `<tr><td><code>${esc(name)}</code></td><td class="text-muted small">${desc}</td></tr>`;
  const paramTable = (rows) => `
    <table class="table table-sm align-middle mb-3"><thead><tr>
      <th style="width:220px;">Query parameter</th><th>Description</th>
    </tr></thead><tbody>${rows}</tbody></table>`;
  const fieldRow = (name, desc) => `<tr><td><code>${esc(name)}</code></td><td class="text-muted small">${desc}</td></tr>`;

  const assetJson = `{
  "assets": [
    {
      "id": "101103db-2e78-41d7-a8e4-06f9534ca2e5",
      "reference": "GEM-1042",
      "label": "Engine Stand GEM-1042",
      "serial": "SN-20042",
      "status": "active",
      "type": "engine-stand",
      "tenantId": "61423820-...",
      "lastLat": 50.037817,
      "lastLon": 8.562232,
      "lastSeen": "2026-08-24T08:58:52.000Z",
      "sensors": { "temperature": 5.01, "batteryPercent": 77, "loadState": 1, "loadStateLabel": "loaded" },
      "updatedAt": "2026-08-24T08:58:53.120Z"
    }
  ],
  "count": 1,
  "truncated": false
}`;

  const historyJson = `{
  "assetId": "101103db-2e78-41d7-a8e4-06f9534ca2e5",
  "count": 2,
  "history": [
    { "ts": "2026-08-23T06:00:12.000Z", "lat": 50.0378, "lon": 8.5622,
      "sensors": { "temperature": 12.4, "battery": 3.88 } },
    { "ts": "2026-08-23T06:30:09.000Z", "lat": null, "lon": null,
      "sensors": { "temperature": 12.1, "humidity": 54 } }
  ]
}`;

  const hookJson = `{
  "event": "position.updated",
  "occurredAt": "2026-08-24T10:00:00.000Z",
  "tenantId": "61423820-...",
  "data": {
    "assetId": "101103db-...",
    "lat": 50.03, "lon": 8.56,
    "at": "2026-08-24T10:00:00Z",
    "sensors": { "temperature": 21.5, "loadState": 1, "loadStateLabel": "loaded" }
  }
}`;

  const alertJson = `{ "event": "alert.triggered", "occurredAt": "...", "tenantId": "...",
  "data": { "alertId": "...", "assetId": "...", "assetLabel": "Engine Stand GEM-1042",
            "metric": "temperature", "operator": ">", "threshold": 30,
            "value": 31.2, "severity": "warning", "at": "..." } }`;

  const geofenceJson = `{ "event": "geofence.crossed", "occurredAt": "...", "tenantId": "...",
  "data": { "alertId": "...", "assetId": "...", "assetLabel": "Engine Stand GEM-1042",
            "poi": "Frankfurt Airport", "direction": "exit", "at": "..." } }`;

  const verifyJs = `// Verify an incoming webhook (Node.js)
import crypto from 'node:crypto';
const raw = req.rawBody;                       // the exact bytes you received
const expected = 'sha256=' + crypto
  .createHmac('sha256', YOUR_WEBHOOK_SECRET)
  .update(raw)
  .digest('hex');
const ok = crypto.timingSafeEqual(
  Buffer.from(expected), Buffer.from(req.headers['x-superb-signature']));`;

  const syncJs = `// Incremental sync (poll every few minutes)
let lastSync = null;                            // persist between runs
const url = lastSync
  ? \`${base}/assets?since=\${encodeURIComponent(lastSync)}\`
  : \`${base}/assets\`;                          // first run: full list
const started = new Date().toISOString();       // capture BEFORE the request
const res = await fetch(url, { headers: { 'x-api-key': KEY } });
const { assets } = await res.json();
upsert(assets);                                 // empty array = nothing changed
lastSync = started;                             // never miss an update`;

  const errRow = (codeNum, when) => `<tr><td><code>${codeNum}</code></td><td class="text-muted small">${when}</td></tr>`;

  container.innerHTML = `
    <div class="container-fluid py-3" style="max-width:1000px;">
      <div class="mb-3">
        <a href="#manual" class="small text-muted text-decoration-none"><i class="fa-solid fa-arrow-left me-1"></i>Documentation</a>
        <h1 class="h4 mb-1 mt-1"><i class="fa-solid fa-code me-2"></i>API reference</h1>
        <p class="text-muted mb-0">Read your account's asset data and receive webhooks programmatically.</p>
      </div>

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted">Getting started</h2>
        <p class="mb-2">The API is available to accounts with the <b>API add-on</b>. A tenant administrator creates keys under
          <a href="#settings">Settings - API access</a>. A key belongs to exactly <b>one</b> tenant and carries the
          <b>scopes</b> chosen at creation (<code>read:assets</code>, <code>read:history</code>). The full key
          (<code>sk_live_...</code>) is shown <b>once</b> at creation - we store only a hash, so a lost key must be replaced.</p>
        <div class="mb-1"><span class="fw-semibold">Base URL</span></div>
        ${code(base)}
        <div class="mb-1"><span class="fw-semibold">Authentication</span></div>
        <p class="text-muted small mb-2">Send the key as the <code>x-api-key</code> <b>HTTP header</b> on every request.
          It is never accepted as a query parameter (keys in URLs end up in logs and proxies). All endpoints are read-only <code>GET</code>.</p>
        ${code(`curl -H "x-api-key: sk_live_..." \\\n  ${base}/assets`)}
      </div></div>

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted">Rate limits &amp; errors</h2>
        <p class="text-muted small mb-2">Each key has a token bucket of <b>60 requests burst</b>, refilling at <b>~10 requests/second</b>.
          Over the limit returns <code>429</code> with a <code>Retry-After</code> header (seconds). Errors are JSON: <code>{ "error": "..." }</code>.</p>
        <table class="table table-sm align-middle mb-0"><thead><tr><th style="width:90px;">Status</th><th>Meaning</th></tr></thead><tbody>
          ${errRow(400, 'Malformed parameter, e.g. an invalid <code>since</code>/<code>until</code> date (use ISO 8601).')}
          ${errRow(401, 'Missing or invalid <code>x-api-key</code>.')}
          ${errRow(403, 'The API add-on is not enabled for the account, or the key lacks the required scope.')}
          ${errRow(404, 'Unknown endpoint, or the asset does not exist in your tenant.')}
          ${errRow(405, 'Only <code>GET</code> is allowed.')}
          ${errRow(429, 'Rate limit exceeded - honour <code>Retry-After</code>.')}
          ${errRow(500, 'Server-side failure - safe to retry with backoff.')}
        </tbody></table>
      </div></div>

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted">Endpoints</h2>

        ${endpoint('GET', '/assets', 'read:assets')}
        <p class="text-muted small mb-2">All assets of your tenant, in a curated projection (internal identifiers such as device ids or slot state are never exposed).
          At most <b>1000</b> assets are returned; <code>truncated: true</code> signals a cut-off - use <code>?since</code> to narrow.</p>
        ${paramTable(paramRow('since', 'ISO 8601 timestamp. Returns only assets <b>changed since</b> then - the building block for incremental syncs (see recipe below). Omit for the full list.'))}
        <div class="mb-1 fw-semibold">Response</div>
        ${code(assetJson)}
        <table class="table table-sm align-middle mb-2"><thead><tr><th style="width:220px;">Field</th><th>Description</th></tr></thead><tbody>
          ${fieldRow('id', 'Stable asset id - use it for <code>/assets/{id}</code> and <code>/history</code>.')}
          ${fieldRow('reference / label / serial', 'Your identifiers as maintained in the platform (any may be <code>null</code>).')}
          ${fieldRow('status / type', '<code>active</code> | <code>inactive</code>; asset type such as <code>engine-stand</code>, <code>apu</code>, <code>other</code>.')}
          ${fieldRow('lastLat / lastLon', 'Last known position (<code>null</code> if the asset has never reported one).')}
          ${fieldRow('lastSeen', 'Timestamp of the last report from any device on the asset.')}
          ${fieldRow('sensors', 'Latest reading per metric, merged across all devices on the asset - see the sensor reference below.')}
          ${fieldRow('updatedAt', 'When the record last changed server-side - compare against your <code>since</code> cursor.')}
        </tbody></table>

        ${endpoint('GET', '/assets/{id}', 'read:assets')}
        <p class="text-muted small mb-2">One asset in the same projection, wrapped as <code>{ "asset": { ... } }</code>. Assets outside your tenant return <code>404</code>.</p>

        ${endpoint('GET', '/assets/{id}/history', 'read:history')}
        <p class="text-muted small mb-2">Position and sensor history, oldest first. Defaults to the <b>last 14 days</b>; one request spans at most
          <b>92 days</b> (a longer span is clamped, keeping the newer end). Older data is served transparently from the archive - page backwards with several requests.</p>
        ${paramTable(
          paramRow('since', 'ISO 8601 - start of the window. Default: 14 days ago.') +
          paramRow('until', 'ISO 8601 - end of the window. Default: now.')
        )}
        <div class="mb-1 fw-semibold">Response</div>
        ${code(historyJson)}
        <p class="text-muted small mb-0"><code>lat</code>/<code>lon</code> are <code>null</code> on heartbeat rows that carried only sensor readings
          (e.g. a wireless sensor reporting without a GPS fix) - filter on them if you only want positions.</p>
      </div></div>

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted">Sensor reference</h2>
        <p class="text-muted small mb-2">The <code>sensors</code> object carries the latest value per metric, merged across all devices on the asset.
          Only these metrics appear (when the hardware provides them):</p>
        ${code(`temperature   probe1Temp   probe2Temp   humidity    pressure
speed         fuelLevel    distanceM    rangeMm     peakG
battery       batteryPercent   rssi     gsmSignal   magnetCount`)}
        <p class="text-muted small mb-0"><b>Derived sensors</b> (your configured threshold rules, e.g. a loaded/empty state) appear as a numeric
          <code>0/1</code> value plus a companion <code>&lt;key&gt;Label</code> string, e.g. <code>"loadState": 1, "loadStateLabel": "loaded"</code>.</p>
      </div></div>

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted">Incremental sync recipe</h2>
        <p class="text-muted small mb-2">Poll <code>/assets?since=</code> with the timestamp captured <b>before</b> your previous request -
          an overlap is harmless (idempotent upserts), a gap is not. An empty <code>assets</code> array means nothing changed.</p>
        ${code(syncJs)}
      </div></div>

      <div class="card shadow-sm mb-4"><div class="card-body">
        <h2 class="h6 text-uppercase text-muted">Webhooks</h2>
        <p class="mb-2">Instead of polling, we can POST signed JSON events to your endpoints. Configure them (URL, signing secret, subscribed events)
          under <a href="#settings">Settings - API access</a>; several endpoints per account are supported, and a test event can be sent from there.</p>
        <table class="table table-sm align-middle mb-3"><thead><tr><th style="width:220px;">Event</th><th>Fired</th></tr></thead><tbody>
          ${fieldRow('position.updated', 'Once per device uplink that reported a position (a buffered upload fires one event for its newest position).')}
          ${fieldRow('alert.triggered', 'When one of your threshold alert rules starts firing.')}
          ${fieldRow('geofence.crossed', 'When an asset enters or exits a geofenced POI (<code>direction</code>: <code>enter</code> | <code>exit</code>).')}
          ${fieldRow('test.ping', 'Manually from the settings page, to verify your endpoint.')}
        </tbody></table>
        <div class="mb-1 fw-semibold">Envelope - every event has the same outer shape</div>
        ${code(hookJson)}
        <div class="mb-1 fw-semibold">alert.triggered data</div>
        ${code(alertJson)}
        <div class="mb-1 fw-semibold">geofence.crossed data</div>
        ${code(geofenceJson)}
        <div class="mb-1 fw-semibold">Headers &amp; signature</div>
        <p class="text-muted small mb-2">Each request carries <code>x-superb-event</code> (the event name) and
          <code>x-superb-signature: sha256=&lt;hmac&gt;</code> - the HMAC-SHA256 of the exact request body using your endpoint's signing secret.
          Always verify it, in constant time:</p>
        ${code(verifyJs)}
        <div class="mb-1 fw-semibold">Delivery semantics</div>
        <p class="text-muted small mb-0">Delivery is <b>best-effort</b>: one attempt per endpoint with a 5-second timeout, <b>no retry</b>.
          Treat webhooks as a low-latency signal and reconcile with <code>/assets?since=</code> to close any gaps. Respond with a <code>2xx</code> quickly;
          do your processing asynchronously.</p>
      </div></div>
    </div>`;
}
